import { createFileRoute, Link } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
  head: () => ({
    meta: [
      { title: "Terms & Conditions — JobYoda" },
      { name: "description", content: "JobYoda terms of service and conditions of use." },
      { property: "og:title", content: "Terms & Conditions — JobYoda" },
      { property: "og:description", content: "JobYoda terms of service and conditions of use." },
    ],
  }),
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" component="div">
        {children}
      </Typography>
    </Box>
  );
}

function TermsPage() {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Button component={Link} to="/" size="small" color="inherit" sx={{ mb: 2 }}>
          ← Back to home
        </Button>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          Terms & Conditions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </Typography>
        <Divider sx={{ my: 3 }} />

        <Typography variant="body1" color="text.secondary">
          These Terms & Conditions ("Terms") govern your use of the JobYoda service
          ("JobYoda", the "service"), operated by <strong>Hypertime LLC</strong>
          {" "}("we", "us"), trading as JobYoda. By accessing or using the service you
          agree to these Terms. Please read them carefully.
        </Typography>

        <Section title="1. Use of service">
          You must be at least 16 years old to use JobYoda. You are responsible for
          keeping your account credentials secure and for all activity under your
          account. You agree to provide accurate information and to keep it updated.
        </Section>

        <Section title="2. Your data">
          You retain full ownership of the data you upload — resume, profile,
          applications, notes, and generated materials. You grant Hypertime LLC a
          limited, non-exclusive license to host and process this data solely to
          deliver the service to you (matching, generation, tracking).
        </Section>

        <Section title="3. AI-generated content">
          JobYoda uses AI to generate resumes, cover letters, and answers grounded in
          your verified profile. You are responsible for reviewing all output before
          sending it to an employer, for ensuring you have the right to any input
          content you provide, and for not using outputs to misrepresent your
          experience. We do not guarantee outcomes such as interviews or offers, and
          AI outputs may contain inaccuracies.
        </Section>

        <Section title="4. Acceptable use">
          You must not misuse the service. In particular, you must not: (a) use the
          service unlawfully or for fraud or spam; (b) infringe any third party's
          intellectual property; (c) interfere with the security of the service,
          including probing, scanning, scraping, or introducing malware; (d) fabricate
          experience or attempt to deceive employers; (e) reverse-engineer, resell, or
          redistribute the service; or (f) share your account with others.
        </Section>

        <Section title="5. Payments, subscriptions and refunds">
          Paid plans are billed by Hypertime LLC using{" "}
          <a href="https://stripe.com" target="_blank" rel="noreferrer" style={{ color: "inherit", fontWeight: 600 }}>Stripe</a>{" "}
          as our payment processor. Subscriptions renew automatically until
          cancelled. You can cancel anytime from your account billing page;
          access continues until the end of the current billing period. We
          offer a 30-day money-back guarantee — see our{" "}
          <Link to="/refunds" style={{ color: "inherit", fontWeight: 600 }}>Refund Policy</Link>{" "}
          for how to request a refund.
        </Section>

        <Section title="6. Payment processing">
          Card payments are processed by <strong>Stripe</strong>. Your card
          details are handled by Stripe under its own terms and privacy policy —
          we never receive or store full card numbers. Applicable taxes are
          collected where required and remitted by Hypertime LLC.
        </Section>


        <Section title="7. Intellectual property">
          The service, including its software, design, branding, and documentation, is
          owned by Hypertime LLC and protected by intellectual property laws. We grant
          you a limited, non-exclusive, non-transferable right to use the service
          within your selected plan. All rights not expressly granted are reserved.
        </Section>

        <Section title="8. Suspension and termination">
          We may suspend or terminate your access for material breach of these Terms,
          non-payment, security or fraud risk, or repeated or serious policy
          violations. You can delete your account and your data at any time from
          settings. On termination, your access ends and your data will be deleted in
          accordance with our{" "}
          <Link to="/privacy" style={{ color: "inherit", fontWeight: 600 }}>Privacy Notice</Link>.
        </Section>

        <Section title="9. Disclaimer and liability">
          The service is provided "as is" and "as available" without warranties of any
          kind, whether express or implied, including merchantability, fitness for a
          particular purpose, and non-infringement, to the fullest extent permitted by
          law. We do not warrant that the service will be uninterrupted or error-free.
          To the maximum extent permitted by law, Hypertime LLC is not liable for
          indirect, incidental, special, consequential, or punitive damages, including
          loss of profits, data, goodwill, or missed job opportunities. Nothing in
          these Terms excludes liability for fraud, death, or personal injury caused
          by negligence where such exclusion is prohibited by law.
        </Section>

        <Section title="10. Indemnity">
          You agree to indemnify and hold Hypertime LLC harmless from claims arising
          out of content you submit, your unlawful use of the service, or your breach
          of these Terms.
        </Section>

        <Section title="11. Changes">
          We may update these Terms. Material changes will be communicated by email or
          in-app notice. Continued use after changes constitutes acceptance.
        </Section>

        <Section title="12. Governing law">
          These Terms are governed by the laws of the State of Delaware, USA, without
          regard to its conflict of law principles. Disputes will be resolved in the
          courts of Delaware, unless mandatory consumer protection law in your country
          of residence requires otherwise.
        </Section>

        <Section title="13. Contact">
          Hypertime LLC — questions? Visit our{" "}
          <Link to="/support" style={{ color: "inherit", fontWeight: 600 }}>support page</Link>.
        </Section>

        <Divider sx={{ my: 5 }} />
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button component={Link} to="/privacy" variant="outlined">Privacy</Button>
          <Button component={Link} to="/refunds" variant="outlined">Refund Policy</Button>
          <Button component={Link} to="/support" variant="outlined">Support</Button>
          <Button component={Link} to="/signup" variant="contained">Get started</Button>
        </Stack>
      </Container>
    </Box>
  );
}
